import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, CalendarCheck, BarChart2, BarChart3, Users, Target,
  UtensilsCrossed, Ban, ArrowRight, Mail, Building2, QrCode,
  ClipboardList, AlertCircle, Bell, Truck, Star, DollarSign,
  MessageCircle, ShoppingBag, Package, CreditCard, Tags,
  Settings, Sparkles, ChefHat, MessagesSquare, Clock, Hand, Zap, UserCog, Users2, Ticket,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { reservasApi, eventosApi, tareasApi, walkinApi, eventosPublicosApi } from "../lib/api";
import {
  puedeReportes, puedeClientes, puedeConfigurarSucursal, ROLE_LABEL,
  esCocina, esOperativo, puedeGestionarReservas, puedeVerReservas, puedeOperarNegocio,
  puedeVerEncuestaQr, esMaestro, moduloActivo, accesoEfectivo, puedeGestionarUsuarios,
} from "../lib/permisos";
import { LiveClock } from "../components/LiveClock";

interface MenuItem {
  icon: typeof Users;
  name: string;
  desc: string;
  to: string;
  accent: string;
  section: SectionKey;
  badge?: number;
  show: boolean;
  sprint?: string;
}

type SectionKey = "operacion" | "delivery" | "clientes" | "config" | "futuro";

interface SectionMeta {
  key: SectionKey;
  title: string;
  subtitle: string;
  icon: typeof Users;
  accent: string;
}

const SECCIONES: SectionMeta[] = [
  {
    key: "operacion",
    title: "Operación del día",
    subtitle: "Reservas, calendario, eventos privados y tareas del equipo",
    icon: Calendar,
    accent: "#F59E0B",
  },
  {
    key: "delivery",
    title: "Delivery & Catálogo",
    subtitle: "Pedidos a domicilio, menú digital, artículos y métodos de pago",
    icon: ShoppingBag,
    accent: "#F97316",
  },
  {
    key: "clientes",
    title: "Inteligencia & Comunicación",
    subtitle: "Cliente 360°, reportes, mensajería automática y códigos QR",
    icon: Sparkles,
    accent: "#3B82F6",
  },
  {
    key: "config",
    title: "Configuración",
    subtitle: "Personalización de la sucursal y datos generales",
    icon: Settings,
    accent: "#6366F1",
  },
  {
    key: "futuro",
    title: "Próximamente",
    subtitle: "Módulos en desarrollo que se activarán pronto",
    icon: Sparkles,
    accent: "#8B5CF6",
  },
];

function formatFecha() {
  return new Date().toLocaleDateString("es-VE", {
    weekday: "long", day: "numeric", month: "long",
  });
}

interface TareaItem {
  id: number; nombre: string; fecha: string; horaInicio: string;
  itemId: string; itemText: string; itemEstado: string; fechaEntrega?: string;
}

export default function TiendaDashboard() {
  const { user } = useAuth();
  const [pendientes, setPendientes] = useState<number | null>(null);
  const [walkinEsperando, setWalkinEsperando] = useState<number | null>(null);
  const [pagosEventosPendientes, setPagosEventosPendientes] = useState<number | null>(null);
  const [misTareas, setMisTareas] = useState<TareaItem[]>([]);
  const [tareasOp, setTareasOp] = useState<any[]>([]);

  const cargarDashboard = () => {
    if (puedeGestionarReservas(user) || puedeVerReservas(user)) {
      reservasApi.countPendientes().then(r => setPendientes(r.count)).catch(() => setPendientes(null));
    }
    if (puedeOperarNegocio(user)) {
      walkinApi.notificaciones().then(w => setWalkinEsperando(w.length)).catch(() => setWalkinEsperando(null));
      eventosPublicosApi.resumen().then(r => setPagosEventosPendientes(r.pagosPendientes)).catch(() => setPagosEventosPendientes(null));
    }
    eventosApi.misTareas?.()
      .then((t: any[]) => setMisTareas(Array.isArray(t) ? t : []))
      .catch(() => setMisTareas([]));
    tareasApi.mias()
      .then(t => setTareasOp(Array.isArray(t) ? t : []))
      .catch(() => setTareasOp([]));
  };

  useEffect(() => {
    cargarDashboard();
    const interval = setInterval(cargarDashboard, 30_000);
    const onVisible = () => { if (document.visibilityState === "visible") cargarDashboard(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const items: MenuItem[] = [
    // ── OPERACIÓN ──
    {
      icon: CalendarCheck, name: "Reservas",
      desc: "Confirmar, gestionar y dar seguimiento",
      to: "/reservas", accent: "#F59E0B",
      section: "operacion",
      badge: pendientes ?? undefined,
      show: puedeVerReservas(user),
    },
    {
      icon: Clock, name: "Lista de espera",
      desc: "Reservas en cola por capacidad llena",
      to: "/tienda/lista-espera", accent: "#EAB308",
      section: "operacion",
      show: puedeVerReservas(user),
    },
    {
      icon: DollarSign, name: "Pagos de reservas",
      desc: "Aprobá pagos reportados por clientes",
      to: "/tienda/pagos-reservas", accent: "#10B981",
      section: "operacion",
      show: puedeVerReservas(user),
    },
    {
      icon: Users2, name: "Lista de espera (walk-in)",
      desc: "Clientes sin reserva anotados por QR en la puerta",
      to: "/tienda/walkin", accent: "#C0392B",
      section: "operacion",
      badge: walkinEsperando ?? undefined,
      show: puedeOperarNegocio(user),
    },
    {
      icon: Calendar, name: "Calendario",
      desc: "Vista diaria y mensual de ocupación",
      to: "/calendario", accent: "#6366F1",
      section: "operacion", show: true,
    },
    {
      icon: BarChart2, name: "Ocupación",
      desc: "Vista en tiempo real de pistas y mesas",
      to: "/tienda/ocupacion", accent: "#8B5CF6",
      section: "operacion", show: true,
    },
    {
      icon: Users, name: "Eventos privados",
      desc: "Fiestas, grupos, invitados y check-in",
      to: "/tienda/eventos", accent: "#0EA5E9",
      section: "operacion", show: true,
    },
    {
      icon: Ticket, name: "Eventos públicos",
      desc: "Venta de entradas con cupo reservado hasta confirmar el pago",
      to: "/tienda/eventos-publicos", accent: "#7C3AED",
      section: "operacion",
      badge: pagosEventosPendientes ?? undefined,
      show: puedeOperarNegocio(user),
    },
    {
      icon: Bell, name: "Avisos y tareas",
      desc: "Recordatorios y tareas del equipo",
      to: "/tienda/tareas", accent: "#7C3AED",
      section: "operacion", show: true,
    },
    {
      icon: Ban, name: "Bloqueos",
      desc: "Bloquear mesas, zonas o períodos",
      to: "/tienda/bloqueos", accent: "#EF4444",
      section: "operacion",
      show: puedeConfigurarSucursal(user),
    },

    // ── DELIVERY & CATÁLOGO ──
    {
      icon: ShoppingBag, name: "Delivery",
      desc: "Tablero de pedidos en tiempo real",
      to: "/delivery", accent: "#F97316",
      section: "delivery",
      show: puedeClientes(user),
    },
    {
      icon: UtensilsCrossed, name: "Menú digital",
      desc: "Cartas PDF y QR para los clientes",
      to: "/tienda/menu-config", accent: "#EC4899",
      section: "delivery",
      show: puedeConfigurarSucursal(user),
    },
    {
      icon: Package, name: "Configurar artículos",
      desc: "Foto, precios, agotados y destacados",
      to: "/articulos-config", accent: "#8B5CF6",
      section: "delivery",
      show: puedeConfigurarSucursal(user),
    },
    {
      icon: Tags, name: "Categorías",
      desc: "Agrupar artículos para menú y delivery",
      to: "/tienda/categorias", accent: "#A855F7",
      section: "delivery",
      show: puedeConfigurarSucursal(user),
    },
    {
      icon: CreditCard, name: "Pasarelas de pago",
      desc: "PayPal, Pago Móvil, Zelle, Stripe...",
      to: "/pasarelas-pago", accent: "#06B6D4",
      section: "delivery",
      show: puedeConfigurarSucursal(user),
    },
    {
      icon: Truck, name: "Zonas de delivery",
      desc: "Municipios/sectores con costo de envío",
      to: "/tienda/delivery-zonas", accent: "#10B981",
      section: "delivery",
      show: puedeConfigurarSucursal(user),
    },

    // ── INTELIGENCIA & COMUNICACIÓN ──
    {
      icon: Users, name: "Cliente 360°",
      desc: "Historial, hábitos y estadísticas",
      to: "/cliente360", accent: "#3B82F6",
      section: "clientes",
      show: puedeClientes(user),
    },
    {
      icon: Target, name: "Audiencias",
      desc: "Segmentos y campañas por consumo",
      to: "/audiencias", accent: "#8B5CF6",
      section: "clientes",
      show: puedeClientes(user),
    },
    {
      icon: BarChart3, name: "Reportes",
      desc: "Métricas, análisis y estadísticas",
      to: "/reportes", accent: "#10B981",
      section: "clientes",
      show: puedeReportes(user),
    },
    {
      icon: QrCode, name: "Códigos QR",
      desc: "QR de reservas, menú y mesas",
      to: "/tienda/qr", accent: "#0EA5E9",
      section: "clientes",
      show: puedeOperarNegocio(user),
    },
    {
      icon: Mail, name: "Plantillas de email",
      desc: "Mensajes automáticos personalizados",
      to: "/tienda/email-templates", accent: "#8B5CF6",
      section: "clientes",
      show: puedeConfigurarSucursal(user),
    },
    {
      icon: MessageCircle, name: "WhatsApp Bot",
      desc: "Reservas, confirmaciones y notificaciones",
      to: "/tienda/whatsapp", accent: "#25D366",
      section: "clientes",
      show: puedeConfigurarSucursal(user) || !!user?.superAdmin,
    },
    {
      icon: Star, name: "Encuestas & feedback",
      desc: "Creá encuestas y enviálas por WhatsApp",
      to: "/tienda/encuestas", accent: "#EAB308",
      section: "clientes",
      show: puedeConfigurarSucursal(user),
    },
    {
      icon: QrCode, name: "Encuestas (QR)",
      desc: "Mostrale el QR al cliente para que la responda",
      to: "/encuestas-qr", accent: "#EAB308",
      section: "clientes",
      show: !puedeConfigurarSucursal(user) && puedeVerEncuestaQr(user),
    },

    // ── CONFIGURACIÓN ──
    {
      icon: UserCog, name: "Usuarios",
      desc: "Equipo, roles y permisos por usuario",
      to: "/usuarios", accent: "#6366F1",
      section: "config",
      // Nota: "/usuarios" a propósito no está en CATALOGO_RUTAS (usuarios.tsx)
      // -- nadie puede forzar este acceso vía override desde la pantalla de
      // gestión, para no abrir una vía circular de escalar privilegios.
      show: puedeGestionarUsuarios(user),
    },
    {
      icon: Building2, name: "Mi sucursal",
      desc: "Logo, colores, zonas y contacto",
      to: "/sucursales", accent: "#6366F1",
      section: "config",
      show: puedeConfigurarSucursal(user),
    },
    {
      icon: Settings, name: "Módulos del cliente",
      desc: "Activar/desactivar módulos de esta organización",
      to: "/tienda/modulos", accent: "#0EA5E9",
      section: "config",
      show: esMaestro(user) || user?.role === "gerencia",
    },
    {
      icon: Settings, name: "Módulos (todos los clientes)",
      desc: "Panel global: módulos por organización",
      to: "/admin/modulos", accent: "#0EA5E9",
      section: "config",
      show: !!user?.superAdmin,
    },

    // ── PRÓXIMOS SPRINTS ──
    {
      icon: DollarSign, name: "Turnos & nómina",
      desc: "Planificación, horas y asistencia",
      to: "#", accent: "#8B5CF6",
      section: "futuro",
      show: puedeConfigurarSucursal(user) || !!user?.superAdmin,
      sprint: "Sprint 6",
    },
  ];

  const firstName = user?.fullName?.split(" ")?.[0] ?? "hola";
  // Módulos que pueden apagarse por organización (clave en crm_organizaciones.modulos).
  const MODULO_POR_RUTA: Record<string, string> = {
    "/delivery": "delivery",
    "/tienda/delivery-zonas": "delivery",
    "/tienda/pagos-reservas": "pagos_reservas",
    "/tienda/walkin": "walkin",
    "/tienda/encuestas": "encuestas",
    "/encuestas-qr": "encuestas",
    "/tienda/eventos": "eventos",
    "/tienda/eventos-publicos": "eventos_publicos",
    "/audiencias": "audiencias",
    "/cliente360": "cliente360",
    "/reportes": "reportes",
    "/tienda/whatsapp": "whatsapp",
    "/tienda/qr": "qr",
    "/calendario": "calendario",
  };
  const visibles = items
    .filter(i => accesoEfectivo(user, i.to, i.show))
    .filter(i => {
      const m = MODULO_POR_RUTA[i.to];
      return !m || moduloActivo(user, m);
    });
  const porSeccion = (key: SectionKey) => visibles.filter(i => i.section === key);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 animate-fade-in">

      {/* ════ HERO ════ */}
      <header className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-ink-400">
              {formatFecha()}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink-900 mt-1">
              Hola, {firstName}{" "}
              <Hand className="inline-block align-middle text-amber-500 animate-wiggle" size={30} strokeWidth={2} aria-hidden />
            </h1>
            <p className="text-sm text-ink-500 mt-1 inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {ROLE_LABEL[user?.role ?? ""] ?? user?.role}
            </p>
          </div>
          <LiveClock className="hidden sm:block" />
        </div>
      </header>

      {/* ════ QUICK STATS ════ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 tab:grid-cols-3 gap-3 mb-10">
        <QuickStat
          icon={<CalendarCheck className="size-4" />}
          color="amber"
          label="Reservas"
          count={pendientes ?? 0}
          countLabel="pendientes"
          emptyMsg="Sin reservas pendientes"
          linkTo="/reservas?tab=pendientes"
        >
          {pendientes && pendientes > 0 ? (
            <Link to="/reservas?tab=pendientes" className="flex items-center gap-2 px-4 py-2.5 hover:bg-amber-100/50 transition-colors group">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
              <span className="text-sm text-amber-800 font-medium">
                {pendientes} pendiente{pendientes !== 1 ? "s" : ""} de confirmar
              </span>
              <ArrowRight className="size-3.5 text-amber-500 ml-auto shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          ) : null}
        </QuickStat>

        <QuickStat
          icon={<ClipboardList className="size-4" />}
          color="amber"
          label="Mis tareas"
          count={misTareas.length}
          countLabel="asignadas"
          emptyMsg="Sin tareas pendientes — estás al día"
          linkTo="/tienda/eventos"
        >
          {misTareas.slice(0, 3).map((t) => (
            <Link key={t.itemId} to="/tienda/eventos"
              className="flex items-center gap-3 px-4 py-2 hover:bg-amber-100/50 transition-colors group">
              <AlertCircle className="size-3.5 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-800 truncate">{t.itemText}</p>
                <p className="text-[11px] text-ink-500 truncate">
                  {t.nombre} · {new Date(t.fecha + "T00:00:00").toLocaleDateString("es-VE",
                    { weekday: "short", day: "numeric", month: "short" })}
                </p>
              </div>
              <ArrowRight className="size-3.5 text-ink-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          ))}
          {misTareas.length > 3 && (
            <Link to="/tienda/eventos" className="block px-4 py-1.5 text-xs text-amber-700 text-center font-medium hover:bg-amber-100/50 transition-colors">
              +{misTareas.length - 3} más
            </Link>
          )}
        </QuickStat>

        <QuickStat
          icon={<Bell className="size-4" />}
          color="violet"
          label="Avisos"
          count={tareasOp.length}
          countLabel="recordatorios"
          emptyMsg="Sin avisos nuevos"
          linkTo="/tienda/tareas"
          onRefresh={cargarDashboard}
        >
          {tareasOp.slice(0, 3).map((t: any) => (
            <Link key={t.id} to="/tienda/tareas"
              className="flex items-start gap-3 px-4 py-2 hover:bg-violet-100/50 transition-colors group">
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                t.prioridad === "urgente" ? "bg-red-500" :
                t.prioridad === "alta" ? "bg-orange-400" :
                t.prioridad === "normal" ? "bg-violet-500" : "bg-slate-400"
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-800 truncate">{t.titulo}</p>
                <p className="text-[11px] text-ink-500">
                  De {t.creadoPorNombre}
                  {t.prioridad === "urgente" && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 text-red-600 font-semibold">
                      · <Zap size={11} className="inline" aria-hidden /> Urgente
                    </span>
                  )}
                  {t.prioridad === "alta" && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 text-orange-500 font-medium">
                      · <ArrowRight size={11} className="-rotate-90 inline" aria-hidden /> Alta
                    </span>
                  )}
                </p>
              </div>
              <ArrowRight className="size-3.5 text-ink-400 shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          ))}
          {tareasOp.length > 3 && (
            <Link to="/tienda/tareas" className="block px-4 py-1.5 text-xs text-violet-700 text-center font-medium hover:bg-violet-100/50 transition-colors">
              +{tareasOp.length - 3} más
            </Link>
          )}
        </QuickStat>
      </div>

      {/* ════ SECCIONES ════ */}
      {SECCIONES.map((sec) => {
        const sectionItems = porSeccion(sec.key);
        if (sectionItems.length === 0) return null;
        return <Section key={sec.key} meta={sec} items={sectionItems} />;
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Section: panel envuelto con fondo tintado al color de la sección
// ════════════════════════════════════════════════════════════
function Section({ meta, items }: { meta: SectionMeta; items: MenuItem[] }) {
  return (
    <section
      className="mb-6 rounded-2xl overflow-hidden relative"
      style={{
        // Fondo sutil del color de la sección (~7% opacidad) sobre paper
        backgroundColor: `${meta.accent}12`,
        // Borde un poco más definido del mismo color
        boxShadow: `inset 0 0 0 1px ${meta.accent}24`,
      }}
    >
      {/* Acento vertical izquierdo de color sólido para identificar la sección */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: meta.accent }}
        aria-hidden="true"
      />

      {/* Contenido con padding generoso para que el fondo se respire */}
      <div className="p-4 sm:p-5 pl-5 sm:pl-6">
        {/* Header de sección */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="size-10 rounded-xl flex items-center justify-center shrink-0"
            style={{
              backgroundColor: `${meta.accent}1F`,
              color: meta.accent,
              boxShadow: `0 1px 2px ${meta.accent}26`,
            }}
          >
            <meta.icon className="size-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-xl text-ink-900 leading-tight tracking-tight">
              {meta.title}
            </h2>
            <p className="text-xs text-ink-500 mt-0.5">{meta.subtitle}</p>
          </div>
          <span
            className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full"
            style={{
              backgroundColor: `${meta.accent}14`,
              color: meta.accent,
            }}
          >
            {items.length} {items.length === 1 ? "módulo" : "módulos"}
          </span>
        </div>

        {/* Grid de cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 tab:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {items.map((m) => <ModuleCard key={m.name} m={m} />)}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════
// ModuleCard
// ════════════════════════════════════════════════════════════
function ModuleCard({ m }: { m: MenuItem }) {
  const disabled = !!m.sprint;
  const inner = (
    <>
      {/* Glow on hover */}
      {!disabled && (
        <div
          className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl blur-sm"
          style={{ background: `radial-gradient(at top left, ${m.accent}30, transparent 70%)` }}
        />
      )}

      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div
            className={`size-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${disabled ? "grayscale opacity-50" : "shadow-sm"}`}
            style={{ background: `${m.accent}18`, color: m.accent }}
          >
            <m.icon className="size-5" />
          </div>

          {m.sprint && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-ink-100 text-ink-500 border border-ink-200">
              {m.sprint}
            </span>
          )}
          {m.badge !== undefined && m.badge > 0 && (
            <span
              className="text-xs px-2.5 py-1 rounded-full font-bold text-white shadow-sm animate-pulse"
              style={{ background: m.accent }}
            >
              {m.badge}
            </span>
          )}
        </div>

        <h3 className={`font-bold mb-1 leading-tight ${disabled ? "text-ink-500" : "text-ink-900"}`}>
          {m.name}
        </h3>
        <p className="text-xs text-ink-500 leading-relaxed line-clamp-2 min-h-[2.4em]">{m.desc}</p>

        <div className={`flex items-center gap-1 mt-3 text-xs font-medium transition-opacity ${disabled ? "text-ink-400" : "opacity-0 group-hover:opacity-100"}`}
          style={disabled ? {} : { color: m.accent }}>
          {disabled ? "Próximamente" : <>Abrir <ArrowRight className="size-3 group-hover:translate-x-0.5 transition-transform" /></>}
        </div>
      </div>
    </>
  );

  if (disabled) {
    return (
      <div className="group relative bg-white rounded-2xl p-4 border border-ink-200/40 opacity-60 overflow-hidden">
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={m.to}
      className="group relative bg-white rounded-2xl p-4 border border-ink-200/50 hover:border-transparent hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
    >
      {inner}
    </Link>
  );
}

// ════════════════════════════════════════════════════════════
// QuickStat
// ════════════════════════════════════════════════════════════
function QuickStat({
  icon, color, label, count, countLabel, emptyMsg, linkTo, onRefresh, children,
}: {
  icon: React.ReactNode;
  color: "amber" | "violet" | "blue" | "emerald";
  label: string;
  count: number;
  countLabel: string;
  emptyMsg: string;
  linkTo: string;
  onRefresh?: () => void;
  children?: React.ReactNode;
}) {
  const isActive = count > 0;
  const colorMap = {
    amber: { border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-700", icon: "text-amber-600", badge: "bg-amber-100 text-amber-700" },
    violet: { border: "border-violet-200", bg: "bg-violet-50", text: "text-violet-700", icon: "text-violet-600", badge: "bg-violet-100 text-violet-700" },
    blue: { border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-700", icon: "text-blue-600", badge: "bg-blue-100 text-blue-700" },
    emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", icon: "text-emerald-600", badge: "bg-emerald-100 text-emerald-700" },
  }[color];

  return (
    <div className={`rounded-xl border overflow-hidden bg-white ${isActive ? colorMap.border : "border-ink-200"}`}>
      <div className={`flex items-center justify-between px-4 py-2.5 border-b ${isActive ? `${colorMap.border} ${colorMap.bg}` : "border-ink-100 bg-ink-50/50"}`}>
        <div className="flex items-center gap-2">
          <span className={isActive ? colorMap.icon : "text-ink-400"}>{icon}</span>
          <span className={`text-sm font-semibold ${isActive ? colorMap.text : "text-ink-500"}`}>
            {label}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${isActive ? colorMap.badge : "bg-ink-100 text-ink-500"}`}>
            {count}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {onRefresh && (
            <button onClick={onRefresh} className="text-xs text-ink-300 hover:text-ink-500 transition-colors" title="Actualizar">↻</button>
          )}
          <Link to={linkTo} className={`text-xs ${isActive ? colorMap.text : "text-ink-400"} hover:underline`}>
            Ver →
          </Link>
        </div>
      </div>
      {isActive ? (
        <div className="divide-y divide-ink-100">
          {children}
        </div>
      ) : (
        <div className="px-4 py-3 text-xs text-ink-400 text-center">{emptyMsg}</div>
      )}
    </div>
  );
}
